import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "Instructores";

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ListaInstructores = () => {
  const [instructores, setInstructores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const navigate = useNavigate();

  const authConfig = () => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token") || "";

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloInstructores = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
        permisosData?.permisos ||
        permisosData?.usuario?.modulos ||
        permisosData?.usuario?.permisos ||
        permisosData?.user?.modulos ||
        permisosData?.user?.permisos ||
        [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloInstructores = useMemo(() => getModuloInstructores(), []);

  const puedeLeer =
    moduloInstructores?.puedeLeer === true ||
    moduloInstructores?.leer === true ||
    moduloInstructores?.read === true;

  const puedeCrear =
    moduloInstructores?.puedeCrear === true ||
    moduloInstructores?.crear === true ||
    moduloInstructores?.create === true;

  const puedeActualizar =
    moduloInstructores?.puedeActualizar === true ||
    moduloInstructores?.actualizar === true ||
    moduloInstructores?.editar === true ||
    moduloInstructores?.update === true;

  const puedeEliminar =
    moduloInstructores?.puedeEliminar === true ||
    moduloInstructores?.eliminar === true ||
    moduloInstructores?.delete === true;

  const tieneAccesoModulo =
    puedeLeer || puedeCrear || puedeActualizar || puedeEliminar;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          navigate("/login", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const deleteInstructor = useCallback(
    async (id) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar instructores.",
          "warning"
        );
        return;
      }

      try {
        const result = await Swal.fire({
          title: "¿Está seguro?",
          text: "¡No podrá revertir este proceso!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        setDeletingId(id);

        const { data } = await axios.delete(
          `/api/instructores/${id}`,
          authConfig()
        );

        setInstructores((prev) =>
          prev.filter((instructor) => instructor._id !== id)
        );

        Swal.fire({
          icon: "success",
          title: data?.message || "Instructor eliminado correctamente",
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error("Error al eliminar el instructor:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudo eliminar el instructor.",
          "error"
        );
      } finally {
        setDeletingId(null);
      }
    },
    [puedeEliminar, manejarErrorSesion]
  );

  const getData = useCallback(async () => {
    if (!puedeLeer) {
      setInstructores([]);
      return;
    }

    try {
      setLoading(true);

      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token") || "";

      if (!token) {
        Swal.fire(
          "Sesión no válida",
          "No se encontró el token de autenticación.",
          "warning"
        );

        setInstructores([]);
        return;
      }

      const { data } = await axios.get(
        "/api/instructores/listall",
        authConfig()
      );

      const lista = Array.isArray(data?.instructores)
        ? data.instructores
        : Array.isArray(data)
          ? data
          : [];

      setInstructores([...lista].reverse());
    } catch (error) {
      console.error("Error al cargar los instructores:", error);

      if (manejarErrorSesion(error)) return;

      setInstructores([]);

      Swal.fire(
        "Error",
        error.response?.data?.message || "No se pudo cargar la información.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [puedeLeer, manejarErrorSesion]);

  useEffect(() => {
    if (puedeLeer) {
      getData();
    }
  }, [puedeLeer, getData]);

  const dataTabla = useMemo(
    () =>
      instructores.map((instructor) => ({
        ...instructor,

        imagen: (
          <div className="d-flex justify-content-end justify-content-md-center align-items-center w-100">
            {instructor.imgInstructor ? (
              <img
                src={instructor.imgInstructor}
                alt="Instructor"
                className="rounded-circle border border-2"
                style={{
                  width: 46,
                  height: 46,
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                className="rounded-circle border border-2 d-flex justify-content-center align-items-center text-secondary bg-body-tertiary"
                style={{
                  width: 46,
                  height: 46,
                }}
                title="Sin foto"
              >
                <i className="fa-solid fa-user"></i>
              </div>
            )}
          </div>
        ),

        funciones: (
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            {puedeLeer && (
              <button
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                data-bs-toggle="modal"
                data-bs-target={`#previewFirma${instructor._id}`}
                title="Ver Firma"
                type="button"
                disabled={loading || deletingId === instructor._id}
              >
                <i className="fa-solid fa-eye"></i>
              </button>
            )}

            {puedeActualizar && (
              <button
                className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/instructor/registrarfirma/${instructor._id}`, {
                    state: { instructor },
                  })
                }
                title="Registrar Firma"
                type="button"
                disabled={loading || deletingId === instructor._id}
              >
                <i className="fa-solid fa-signature"></i>
              </button>
            )}

            {puedeLeer && (
              <button
                className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/instructor/listarcontratos/${instructor._id}`)
                }
                title="Contratos"
                type="button"
                disabled={loading || deletingId === instructor._id}
              >
                <i className="fa-solid fa-list-check"></i>
              </button>
            )}

            {puedeActualizar && (
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  navigate(`/instructor/edit/${instructor._id}`, {
                    state: { instructor },
                  })
                }
                title="Editar Instructor"
                type="button"
                disabled={loading || deletingId === instructor._id}
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>
            )}

            {puedeEliminar && (
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => deleteInstructor(instructor._id)}
                title="Eliminar Instructor"
                type="button"
                disabled={loading || deletingId === instructor._id}
              >
                {deletingId === instructor._id ? (
                  <i className="fa-solid fa-spinner fa-spin text-white"></i>
                ) : (
                  <i className="fa-solid fa-trash"></i>
                )}
              </button>
            )}
          </div>
        ),

        modalFirma: (
          <div
            className="modal fade"
            id={`previewFirma${instructor._id}`}
            tabIndex="-1"
            aria-labelledby={`previewFirmaLabel${instructor._id}`}
            aria-hidden="true"
            key={`modal-${instructor._id}`}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 rounded-4 shadow">
                <div className="modal-header border-bottom">
                  <div>
                    <h6
                      className="modal-title fw-semibold mb-0"
                      id={`previewFirmaLabel${instructor._id}`}
                    >
                      Firma del instructor
                    </h6>

                    <div className="small text-secondary">
                      {instructor.nombre1Instructor}{" "}
                      {instructor.apellido1Instructor}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Cerrar"
                  ></button>
                </div>

                <div className="modal-body text-center">
                  {instructor.firmaInstructor ? (
                    <img
                      src={instructor.firmaInstructor}
                      alt="Firma del Instructor"
                      className="img-fluid border p-2 rounded-3 bg-white"
                      style={{ maxHeight: 260 }}
                    />
                  ) : (
                    <div className="py-4">
                      <div className="display-6">✍️</div>
                      <p className="text-muted mb-0">
                        Este instructor no tiene firma registrada.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ),
      })),
    [
      instructores,
      loading,
      deletingId,
      puedeLeer,
      puedeActualizar,
      puedeEliminar,
      navigate,
      deleteInstructor,
    ]
  );

  const columns = useMemo(
    () => [
      { accessor: "imagen" },
      { Header: "Documento", accessor: "documentoInstructor" },
      { Header: "Nombre", accessor: "nombre1Instructor" },
      { Header: "Apellido", accessor: "apellido1Instructor" },
      { Header: "Edad", accessor: "edadInstructor" },
      { Header: "Sexo", accessor: "sexoInstructor" },
      {
        Header: "Email",
        accessor: "emailInstructor",
        width: 280,
        hideHeaderOnMobile: true,
      },
      { Header: "Acciones", accessor: "funciones" },
    ],
    []
  );

  if (!tieneAccesoModulo) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning text-center rounded-4">
          No tiene permisos para acceder al módulo Instructores.
        </div>

        <div className="text-center">
          <button
            className="btn btn-dark mt-3"
            onClick={() => navigate("/inicio")}
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Instructores
                </h5>

                <small className="text-secondary">
                  Gestión institucional y generación de documentos.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  className="btn btn-outline-dark flex-fill"
                  type="button"
                  onClick={() => navigate("/inicio")}
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Inicio
                </button>

                {puedeLeer && (
                  <button
                    className="btn btn-outline-secondary flex-fill"
                    type="button"
                    onClick={getData}
                    title="Actualizar"
                    disabled={loading}
                  >
                    <i
                      className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-rotate"
                        } me-2`}
                    ></i>
                    Actualizar
                  </button>
                )}

                {puedeCrear && (
                  <button
                    className="btn btn-dark flex-fill"
                    type="button"
                    disabled={loading}
                    onClick={() => navigate("/instructor/nuevo")}
                    title="Crear Instructor"
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {!puedeLeer ? (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🔒</div>

                <div className="fw-semibold">
                  No tiene permiso para ver instructores registrados.
                </div>

                {puedeCrear && (
                  <>
                    <div className="small mb-3">
                      Pero sí puede registrar nuevos instructores.
                    </div>

                    <button
                      className="btn btn-dark"
                      type="button"
                      onClick={() => navigate("/instructor/nuevo")}
                    >
                      <i className="fa-solid fa-plus me-2"></i>
                      Nuevo instructor
                    </button>
                  </>
                )}
              </div>
            ) : loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">Cargando instructores…</div>
              </div>
            ) : dataTabla.length > 0 ? (
              <>
                <div className="overflow-hidden">
                  <DataTable columns={columns} data={dataTabla} />
                </div>

                {dataTabla.map((i) => i.modalFirma)}
              </>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">👨‍🏫</div>

                <div className="fw-semibold">
                  No hay instructores registrados
                </div>

                <div className="small mb-3">
                  Crea el primer registro para iniciar la gestión.
                </div>

                {puedeCrear && (
                  <button
                    className="btn btn-dark"
                    type="button"
                    onClick={() => navigate("/instructor/nuevo")}
                  >
                    <i className="fa-solid fa-plus me-2"></i>
                    Registrar instructor
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListaInstructores;